import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthRequiredLink from '../components/AuthRequiredLink';
import { usePageInit } from '../hooks/usePageInit';
import './LoginPage.css';

export default function LoginPage() {
  usePageInit();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // リダイレクト先を取得（アカウント削除ページからの場合は削除ページに戻る）
  const redirectTo = location.state?.from || '/account-deletion';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }

    if (!password) {
      setError('パスワードを入力してください');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      
      // ログイン成功後、ユーザードキュメントを確認して削除申請済みかチェック
      try {
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        if (userDoc.exists()) {
          const data: any = userDoc.data() || {};
          const isDeactivated = (data?.isActive === false) || (data?.accountStatus === 'DEACTIVATED');
          if (isDeactivated) {
            // 削除申請済みの場合はサインアウトしてエラーを投げる
            try {
              await signOut(auth);
            } catch {}
            setError('このアカウントは削除申請済みのため、ログインできません');
            setLoading(false);
            return;
          }
        }
      } catch (checkError: any) {
        // ドキュメント取得失敗などは無視して続行
        console.warn('Error checking user status during sign in:', checkError);
      }

      // ログイン成功、リダイレクト先に移動
      navigate(redirectTo);
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'ログインに失敗しました。メールアドレスとパスワードを確認してください。';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'このメールアドレスは登録されていません。';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'パスワードが正しくありません。';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'メールアドレスの形式が正しくありません。';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'このアカウントは無効化されています。';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'ログイン試行回数が多すぎます。しばらくしてから再試行してください。';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var" id="main-wrap">
        <div className="main-wrap">
          <Header />
          <div className="policy-page">
            <div className="page-wrap">
              <div className="hidden-md-up">
                <div className="logo logo-header">
                  <AuthRequiredLink to="/">
                    <img src="/assets/images/mobile-logo.png" alt="logo" />
                  </AuthRequiredLink>
                </div>
              </div>
              <div className="container inner-wrap max-md">
                <AuthRequiredLink className="waves-effect btn-icon backtohome fragment-fadeUp" to="/">
                  <span>
                    <i className="ion-ios-home-outline"></i>
                    <i className="ion-ios-arrow-thin-left"></i>
                  </span>
                </AuthRequiredLink>
                <div className="card policy-box fragment-fadeUp">
                  <div className="policy-content">
                    <h1 className="policy-title">ログイン</h1>
                    <div className="policy-body">
                      <div className="login-content">
                        <p className="login-description">
                          アカウント削除を行うには、ログインが必要です。
                        </p>

                        {error && (
                          <div className="login-error">
                            <p>{error}</p>
                          </div>
                        )}

                        <form onSubmit={handleSubmit} className="login-form">
                          <div className="form-group">
                            <label htmlFor="email">メールアドレス</label>
                            <input
                              type="email"
                              id="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              disabled={loading}
                              required
                              autoComplete="email"
                              className="form-input"
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="password">パスワード</label>
                            <input
                              type="password"
                              id="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              disabled={loading}
                              required
                              autoComplete="current-password"
                              className="form-input"
                            />
                          </div>

                          <div className="form-actions">
                            <button
                              type="submit"
                              className="btn btn-primary waves-effect"
                              disabled={loading}
                            >
                              {loading ? 'ログイン中...' : 'ログイン'}
                            </button>
                          </div>
                        </form>

                        <div className="login-footer">
                          <p>
                            <AuthRequiredLink to="/">ホームに戻る</AuthRequiredLink>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer withDeco={true} />
        </div>
      </div>
    </div>
  );
}

